// src/components/FeatureMatrix.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getOrgs, listAllFeatures, listFeatureMatrix, bulkUpdateFeature } from "../api";
import ToggleCell from "./ToggleCell";
import "../styles/feature-matrix.css";

export default function FeatureMatrix() {
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [features, setFeatures] = useState([]);
  const [cells, setCells] = useState(() => new Map());
  const [saving, setSaving] = useState(() => new Set());
  const [error, setError] = useState("");

  const FEATURE_COL_WIDTH = 112;
  const ORG_COL_WIDTH = 240;

  const keyFor = (orgId, feature) => `${orgId}::${feature}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [orgList, featList] = await Promise.all([getOrgs(), listAllFeatures()]);
        if (cancelled) return;

        orgList.sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(orgList);
        setFeatures(featList);

        const colResults = await Promise.all(
          featList.map((f) => listFeatureMatrix(f).catch(() => ({ feature: f, items: [] })))
        );
        if (cancelled) return;

        const next = new Map();
        for (const col of colResults) {
          for (const row of col.items || []) {
            next.set(keyFor(row.org_id, col.feature), {
              exists: !!row.exists,
              enabled: row.enabled === null ? null : !!row.enabled,
            });
          }
        }
        setCells(next);
      } catch (e) {
        setError(e?.message || "Failed to load matrix.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = useCallback(
    async (orgId, feature) => {
      const k = keyFor(orgId, feature);
      const current = cells.get(k);
      if (!current || !current.exists) return;

      const nextEnabled = !(current.enabled === true);
      const nextSaving = new Set(saving);
      nextSaving.add(k);
      setSaving(nextSaving);

      const optimistic = new Map(cells);
      optimistic.set(k, { ...current, enabled: nextEnabled });
      setCells(optimistic);

      try {
        await bulkUpdateFeature(feature, [orgId], nextEnabled);
        const fresh = await listFeatureMatrix(feature);
        const match = fresh.items.find((x) => x.org_id === orgId);
        const updated = new Map(optimistic);
        updated.set(k, {
          exists: !!match?.exists,
          enabled: match?.enabled === null ? null : !!match?.enabled,
        });
        setCells(updated);
      } catch (e) {
        const rolledBack = new Map(cells);
        rolledBack.set(k, current);
        setCells(rolledBack);
        alert(`Update failed for ${feature} @ org ${orgId}: ${e?.message || e}`);
      } finally {
        const s = new Set(nextSaving);
        s.delete(k);
        setSaving(s);
      }
    },
    [cells, saving]
  );

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: `${ORG_COL_WIDTH}px repeat(${features.length}, ${FEATURE_COL_WIDTH}px)`,
      "--ff-cols": features.length,
      "--ff-orgw": `${ORG_COL_WIDTH}px`,
      "--ff-colw": `${FEATURE_COL_WIDTH}px`,
    }),
    [features.length]
  );

  if (loading) return <div className="container py-3">Loading feature matrix…</div>;
  if (error) return <div className="container py-3 text-danger">Error: {error}</div>;

  return (
    <div className="container-fluid py-3">
      <h4 className="mb-3">Feature Matrix (Orgs × Features)</h4>

      <div className="ff-matrix-scroll">
        <div className="ff-matrix" style={gridStyle}>
          <div className="ff-head ff-sticky-left">Organization</div>
          {features.map((f) => (
            <div className="ff-head" key={f} title={f}>
              <span className="ff-head-text">{f}</span>
            </div>
          ))}

          {orgs.map((org) => (
            <React.Fragment key={org.id}>
              <div className="ff-cell-org ff-sticky-left">
                {org.name}
              </div>
              {features.map((f) => {
                const k = keyFor(org.id, f);
                const cell = cells.get(k) || { exists: false, enabled: null };
                return (
                  <div className="ff-cell-wrap" key={`${org.id}::${f}`}>
                    <ToggleCell
                      exists={cell.exists}
                      value={cell.enabled}
                      saving={saving.has(k)}
                      onClick={() => handleToggle(org.id, f)}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <small className="text-muted d-block mt-2">
        “—” means the feature row doesn’t exist for that org yet.
      </small>
    </div>
  );
}
